import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks
export const fetchBillingDetails = createAsyncThunk(
  'billing/fetchDetails',
  async (params) => {
    const response = await api.get('/api/billings', { params });
    return response.data;
  }
);

// Updated thunk with a default for "data" and clear promise ordering.
// IMPORTANT: Ensure that when you dispatch this thunk you return the promise (i.e. "return dispatch(performBillingAction(...))")
// so that UI elements (like the spinner in BillingTimeline) correctly await its resolution.
export const performBillingAction = createAsyncThunk(
  'billing/performAction',
  async ({ transactionId, action, data = {} }, { rejectWithValue }) => {
    try {
      console.log('Performing billing action:', { transactionId, action, data });
      const response = await api.post(`/api/billings/${transactionId}/action`, {
        action,
        ...data
      });
      return response.data;
    } catch (error) {
      // Enhanced error handling: structure error output for the UI as needed.
      return rejectWithValue({
        message: error.response?.data?.error || error.message || 'Failed to perform action',
        allowedActions: error.response?.data?.allowed_actions || [],
        status: error.response?.status
      });
    }
  }
);

const initialState = {
  billings: [],
  metrics: {
    totalBilled: 0,
    pendingAmount: 0,
    paidLastMonth: 0,
    averagePaymentTime: '30 days'
  },
  loading: false,
  actionInProgress: null,
  error: null,
  pagination: {
    total: 0,
    pages: 1,
    currentPage: 1
  }
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateBillingTransaction: (state, action) => {
      const updatedTransaction = action.payload;
      // Ensure the proper and consistent key is used to identify the record.
      const index = state.billings.findIndex(b => b.transaction_id === updatedTransaction.id);
      
      if (index !== -1) {
        state.billings[index] = {
          ...state.billings[index],
          status: updatedTransaction.status,
          valid_transitions: updatedTransaction.valid_transitions,
          status_history: updatedTransaction.status_history
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.billings = action.payload.invoices || [];
        state.metrics = action.payload.metrics || initialState.metrics;
        state.pagination = {
          total: action.payload.total || 0,
          pages: action.payload.pages || 1,
          currentPage: action.payload.currentPage || 1
        };
      })
      .addCase(fetchBillingDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(performBillingAction.pending, (state, action) => {
        state.loading = true;
        state.actionInProgress = action.meta.arg.action;
        state.error = null;
      })
      .addCase(performBillingAction.fulfilled, (state, action) => {
        state.loading = false;
        state.actionInProgress = null;
        const updatedTransaction = action.payload.transaction;
        // Make sure you're matching the correct identifier.
        const index = state.billings.findIndex(b => b.id === updatedTransaction.load_id);
        if (index !== -1) {
          state.billings[index] = {
            ...state.billings[index],
            status: updatedTransaction.status,
            transaction_id: updatedTransaction.id,
            transaction_uuid: updatedTransaction.uuid,
            valid_transitions: updatedTransaction.valid_transitions,
            status_history: updatedTransaction.status_history
          };
        }
      })
      .addCase(performBillingAction.rejected, (state, action) => {
        state.loading = false;
        state.actionInProgress = null;
        state.error = typeof action.payload === 'object' 
          ? action.payload.message 
          : action.payload || 'An error occurred';
        // Optionally store allowed actions if needed.
        if (action.payload?.allowedActions) {
          state.allowedActions = action.payload.allowedActions;
        }
      });
  }
});

export const { clearError, updateBillingTransaction } = billingSlice.actions;
export default billingSlice.reducer; 